/* eslint-disable react/no-unused-prop-types,react/no-array-index-key */
import React from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import ContentLoader from 'react-content-loader';
import { formatRoute } from 'react-router-named-routes';
import { withTranslation } from 'react-i18next';
import Check from '@material-ui/icons/Check';
import Delete from '@material-ui/icons/Delete';
import Edit from '@material-ui/icons/Edit';
import LinearProgress from '@material-ui/core/LinearProgress';
import Publish from '@material-ui/icons/CloudUpload';
import Refresh from '@material-ui/icons/Refresh';
import SendToReview from '@material-ui/icons/Visibility';
import Star from '@material-ui/icons/Star';
import UnPublish from '@material-ui/icons/CloudDownload';
import Checkbox from '@material-ui/core/Checkbox';
import { Tooltip, Zoom } from '@material-ui/core';
import withStyles from '@material-ui/core/styles/withStyles';
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import Snackbar from 'components/UI/Snackbar/Snackbar';
import Badge from 'components/UI/Badge/Badge';
import AuthContext from 'helpers/authContext';
import RouterContext from 'helpers/routerContext';
import Paginator from 'components/common/Paginator';
import { LoadingAnimation } from 'components/common/Loading';
import { TableLoader } from 'components/common/TableLoader';
import WithPermission, {
  checkPermissions,
} from 'components/User/WithPermission';
import dateFormat from 'helpers/dateHelper';
import { REVIEWS, USER } from 'routes/paths';
import Filter from 'components/Review/Filter';
import {
  formatMatchedRules,
  getReviewActions,
  reviewActionPermissions,
  reviewActions,
  summarizeReviewComment,
} from 'components/Review/helpers';

const actionIcons = {
  [reviewActions.PUBLISH]: Publish,
  [reviewActions.UNPUBLISH]: UnPublish,
  [reviewActions.REPROCESS]: Refresh,
  [reviewActions.SEND_TO_REVIEW]: SendToReview,
  [reviewActions.EDIT]: Edit,
  [reviewActions.DELETE]: Delete,
};

const actionColors = {
  [reviewActions.PUBLISH]: 'success',
  [reviewActions.UNPUBLISH]: 'danger',
  [reviewActions.REPROCESS]: 'warning',
  [reviewActions.SEND_TO_REVIEW]: 'primary',
  [reviewActions.EDIT]: 'info',
  [reviewActions.DELETE]: 'danger',
};

const bulkReviewActions = [
  reviewActions.PUBLISH,
  reviewActions.UNPUBLISH,
  reviewActions.REPROCESS,
  reviewActions.SEND_TO_REVIEW,
  reviewActions.DELETE,
];

const paginationLoaderPath = [
  'M199,16 L304,16 C307,16 310,19 310,22',
  'L310,46 C310,49 307,52 304,52',
  'L199,52 C196,52 193,49 193,46',
  'L193,22 C193,19 196,16 199,16 Z',
].join(' ');

class ReviewList extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      selectedItems: [],
      showCollapseFilters: true,
    };
  }

  canRunReviewAction = action => {
    const { authContext } = this.props;
    return checkPermissions({ allPermissions: [], ...authContext }, [
      reviewActionPermissions[action],
    ]);
  };

  canSelectReview = node =>
    bulkReviewActions.some(
      action =>
        getReviewActions(node.status).indexOf(action) !== -1 &&
        this.canRunReviewAction(action),
    );

  getSelectableReviewIds = edges =>
    edges
      .filter(({ node }) => this.canSelectReview(node))
      .map(({ node }) => node.id);

  getSelectedReviewIdsForAction = (edges, action) => {
    const { selectedItems } = this.state;
    return edges
      .filter(
        ({ node }) =>
          selectedItems.indexOf(node.id) !== -1 &&
          this.canRunReviewAction(action) &&
          getReviewActions(node.status).indexOf(action) !== -1,
      )
      .map(({ node }) => node.id);
  };

  handleSelectAll = edges => {
    const { selectedItems } = this.state;
    const selectableReviewIds = this.getSelectableReviewIds(edges);
    const allSelectableSelected =
      selectableReviewIds.length > 0 &&
      selectableReviewIds.every(id => selectedItems.indexOf(id) !== -1);

    if (!selectableReviewIds.length || allSelectableSelected) {
      this.setState({ selectedItems: [] });
    } else {
      this.setState({ selectedItems: selectableReviewIds });
    }
  };

  handleSelectItem = node => {
    if (!this.canSelectReview(node)) return;

    const { selectedItems } = this.state;
    const { id } = node;
    if (selectedItems.indexOf(id) !== -1) {
      this.setState({
        selectedItems: selectedItems.filter(item => item !== id),
      });
    } else {
      this.setState({ selectedItems: [...selectedItems, id] });
    }
  };

  handleAction = (action, selectedItems) => {
    const {
      publishReviews,
      unpublishReviews,
      reprocessReviews,
      sendReviewsToReview,
      deleteReviews,
    } = this.props;
    if (action === reviewActions.PUBLISH) publishReviews(selectedItems);
    if (action === reviewActions.UNPUBLISH) unpublishReviews(selectedItems);
    if (action === reviewActions.REPROCESS) reprocessReviews(selectedItems);
    if (action === reviewActions.SEND_TO_REVIEW) {
      sendReviewsToReview(selectedItems);
    }
    if (action === reviewActions.DELETE) deleteReviews(selectedItems);
    this.setState({ selectedItems: [] });
  };

  handleReadReview = id => {
    const history = this.context;
    history.push(formatRoute(REVIEWS.DETAILS, { id }));
  };

  handleReadUser = id => {
    const history = this.context;
    history.push(formatRoute(USER.DETAILS, { id }));
  };

  handleShowFilters = () => {
    const { showCollapseFilters } = this.state;
    this.setState({ showCollapseFilters: !showCollapseFilters });
  };

  renderColumns = edges => {
    const { selectedItems } = this.state;
    const { classes, t } = this.props;
    const selectableReviewIds = this.getSelectableReviewIds(edges);
    const allSelectableSelected =
      selectableReviewIds.length > 0 &&
      selectableReviewIds.every(id => selectedItems.indexOf(id) !== -1);
    return [
      {
        Header: (
          <Checkbox
            onClick={() => this.handleSelectAll(edges)}
            checked={allSelectableSelected}
            disabled={!selectableReviewIds.length}
            id="allCheckbox"
            checkedIcon={<Check className={classes.checkedIcon} />}
            icon={<Check className={classes.uncheckedIcon} />}
            classes={{
              checked: classes.checked,
            }}
          />
        ),
        accessor: 'checkbox',
        sortable: false,
        filterable: false,
        maxWidth: 58,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('reviews.list.headers.review')}
          </span>
        ),
        accessor: 'review',
        sortable: false,
        filterable: false,
        minWidth: 280,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('reviews.list.headers.rules')}
          </span>
        ),
        accessor: 'rules',
        sortable: false,
        filterable: false,
        minWidth: 190,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('reviews.list.headers.reviewer')}
          </span>
        ),
        accessor: 'reviewer',
        sortable: false,
        filterable: false,
        minWidth: 170,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('reviews.list.headers.reviewee')}
          </span>
        ),
        accessor: 'reviewee',
        sortable: false,
        filterable: false,
        minWidth: 170,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('reviews.list.headers.reports')}
          </span>
        ),
        accessor: 'reports',
        sortable: false,
        filterable: false,
        minWidth: 160,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('reviews.list.headers.dates')}
          </span>
        ),
        accessor: 'dates',
        sortable: false,
        filterable: false,
        minWidth: 190,
      },
      {
        Header: (
          <span className={classes.headerField} style={{ textAlign: 'center' }}>
            {t('reviews.list.headers.actions')}
          </span>
        ),
        accessor: 'actions',
        sortable: false,
        filterable: false,
        minWidth: 220,
      },
      {
        expander: true,
        show: false,
      },
    ];
  };

  renderUser = user => {
    const { classes, t } = this.props;
    if (!user) return <span>-</span>;
    const userRate =
      user.rate === null || user.rate === undefined ? '-' : user.rate;
    const reviewCount =
      user.reviewCount === null || user.reviewCount === undefined
        ? 0
        : user.reviewCount;

    return (
      <div data-cy="reviewUserInfo" className={classes.adUserContainer}>
        <WithPermission
          alt={<span>{user.fullname}</span>}
          allowPermissions={['view_user']}
        >
          <Button
            simple
            color="primary"
            className={['read', classes.adBtns, classes.titleBtn].join(' ')}
            onClick={() => this.handleReadUser(user.id)}
          >
            {user.fullname}
          </Button>
        </WithPermission>
        <p>{user.email}</p>
        {user.phone && <p>{user.phone}</p>}
        <p>
          <b>{t('reviews.fields.rating')}:</b> {userRate}
        </p>
        <p>
          <b>{t('reviews.fields.reviewCount')}:</b> {reviewCount}
        </p>
      </div>
    );
  };

  renderRules = matchedRules => {
    const rules = formatMatchedRules(matchedRules);
    if (!rules.length) return <span>-</span>;
    return (
      <div>
        {rules.map((rule, index) => (
          <p key={`review-rule-${index}`} style={{ margin: '0 0 4px' }}>
            {rule}
          </p>
        ))}
      </div>
    );
  };

  renderReports = node => {
    const { t } = this.props;
    return (
      <div>
        <p style={{ margin: 0 }}>
          <Badge color={node.reportCount > 0 ? 'danger' : 'gray'}>
            {node.reportCount}
          </Badge>
        </p>
        {(node.reports || []).slice(0, 3).map(report => (
          <p key={report.id} style={{ margin: '4px 0 0' }}>
            <b>{t(`reviews.reportReasons.${report.reason}`)}</b>
            {report.status &&
              ` - ${t(`reviews.reportStatus.${report.status}`)}`}
          </p>
        ))}
      </div>
    );
  };

  renderDates = node => {
    const { t } = this.props;
    const fields = [
      ['createdOn', 'created'],
      ['updatedOn', 'updated'],
      ['publishedOn', 'published'],
      ['reviewedOn', 'reviewed'],
      ['lastReportedOn', 'lastReported'],
      ['deletedOn', 'deleted'],
    ];
    return (
      <div>
        {fields.map(([field, label]) =>
          node[field] ? (
            <p key={field} style={{ margin: '0 0 4px' }}>
              <b>{t(`reviews.dates.${label}`)}:</b> {dateFormat(node[field], t)}
            </p>
          ) : null,
        )}
      </div>
    );
  };

  renderActionButton = (action, node) => {
    const { classes, t } = this.props;
    const Icon = actionIcons[action];
    const actionLabel = t(`reviews.actions.${action}`);

    if (action === reviewActions.EDIT) {
      return (
        <WithPermission
          key={`${action}-${node.id}`}
          allowPermissions={[reviewActionPermissions[action]]}
        >
          <Tooltip
            placement="top"
            TransitionComponent={Zoom}
            title={
              <React.Fragment>
                {actionLabel}
                <span className={classes.arrow} />
              </React.Fragment>
            }
            classes={{
              tooltip: classes.bootstrapTooltip,
              tooltipPlacementTop: classes.bootstrapPlacementTop,
            }}
          >
            <Button
              justIcon
              onClick={() => this.handleReadReview(node.id)}
              id={`review-${action}-${node.id}`}
              color={actionColors[action]}
            >
              <Icon />
            </Button>
          </Tooltip>
        </WithPermission>
      );
    }

    return (
      <WithPermission
        key={`${action}-${node.id}`}
        allowPermissions={[reviewActionPermissions[action]]}
      >
        <Tooltip
          placement="top"
          TransitionComponent={Zoom}
          title={
            <React.Fragment>
              {actionLabel}
              <span className={classes.arrow} />
            </React.Fragment>
          }
          classes={{
            tooltip: classes.bootstrapTooltip,
            tooltipPlacementTop: classes.bootstrapPlacementTop,
          }}
        >
          <Button
            justIcon
            onClick={() => this.handleAction(action, [node.id])}
            id={`review-${action}-${node.id}`}
            color={actionColors[action]}
          >
            <Icon />
          </Button>
        </Tooltip>
      </WithPermission>
    );
  };

  renderRows = edges => {
    const { selectedItems } = this.state;
    const { classes, t } = this.props;
    const history = this.context;
    return edges.map(({ node }) => {
      const canSelect = this.canSelectReview(node);
      return {
        id: node.id,
        checkbox: (
          <Checkbox
            onClick={() => {
              this.handleSelectItem(node);
            }}
            checked={canSelect && selectedItems.indexOf(node.id) !== -1}
            disabled={!canSelect}
            id={`review-checkbox-${node.id}`}
            checkedIcon={<Check className={classes.checkedIcon} />}
            icon={<Check className={classes.uncheckedIcon} />}
            classes={{
              checked: classes.checked,
            }}
          />
        ),
        review: (
          <div style={{ padding: '8px 0' }} id={`review-title-${node.id}`}>
            <div className={classes.idAndDate}>
              <p>
                <b>ID: {node.id}</b>
                <span style={{ marginLeft: 8 }}>
                  <Badge color="info">
                    {t(`reviews.status.${node.status}`)}
                  </Badge>
                </span>
              </p>
            </div>
            <p
              style={{ margin: '4px 0', display: 'flex', alignItems: 'center' }}
            >
              <Star style={{ width: 16, height: 16, color: '#ffb500' }} />
              <b style={{ marginLeft: 4 }}>{node.rate}</b>
            </p>
            <a
              className={['read', classes.adBtns, classes.titleBtn].join(' ')}
              href={formatRoute(REVIEWS.DETAILS, { id: node.id })}
              onClick={e => {
                e.preventDefault();
                history.push(formatRoute(REVIEWS.DETAILS, { id: node.id }));
              }}
            >
              {summarizeReviewComment(node.comment) ||
                t('reviews.list.emptyComment')}
            </a>
          </div>
        ),
        rules: this.renderRules(node.matchedRules),
        reviewer: this.renderUser(node.reviewer),
        reviewee: this.renderUser(node.reviewee),
        reports: this.renderReports(node),
        dates: this.renderDates(node),
        actions: selectedItems.indexOf(node.id) === -1 && (
          <div className="actions-center">
            {getReviewActions(node.status).map(action =>
              this.renderActionButton(action, node),
            )}
          </div>
        ),
      };
    });
  };

  mapLoadingRows = (itemProcessing, edges) => {
    const loadingRows = {};
    edges.forEach((value, index) => {
      if (itemProcessing.includes(value.node.id)) {
        loadingRows[index] = true;
      }
    });
    return loadingRows;
  };

  renderBulkAction = (action, edges) => {
    const { t } = this.props;
    const Icon = actionIcons[action];
    const selectedReviewIds = this.getSelectedReviewIdsForAction(edges, action);

    if (!selectedReviewIds.length) return null;

    return (
      <WithPermission
        key={`bulk-${action}`}
        allowPermissions={[reviewActionPermissions[action]]}
      >
        <Button
          color={actionColors[action]}
          simple
          onClick={() => this.handleAction(action, selectedReviewIds)}
          id={`reviews-${action}`}
        >
          <Icon />
          {t(`reviews.actions.${action}`)}
        </Button>
      </WithPermission>
    );
  };

  render() {
    const {
      data,
      loading,
      fullLoading,
      changeNumberOfRows,
      changePage,
      numberOfRowsSelected,
      paginationDirection,
      classes,
      filter,
      restartFilters,
      appliedFilter,
      itemProcessing,
      t,
      failedIds,
      showNotification,
      closeNotification,
      refresh,
    } = this.props;
    const { selectedItems, showCollapseFilters } = this.state;
    const reviewsData = data && data.adminReviews;
    const edges = loading || !reviewsData ? [] : reviewsData.edges;
    const rows = this.renderRows(edges);
    const cols = this.renderColumns(edges);
    const loadingRows = this.mapLoadingRows(itemProcessing, edges);

    return (
      <React.Fragment>
        <Snackbar
          place="tc"
          color="danger"
          message={t('common.failed_bulk_action', {
            count: failedIds.length,
          })}
          open={showNotification}
          closeNotification={closeNotification}
          close
        />
        <div className={classes.pageContainer}>
          <GridContainer justify="space-between" alignItems="center">
            <GridItem>
              <h3 className={classes.title}>{t('reviews.list.title')}</h3>
            </GridItem>
            <GridItem>
              <Button color="primary" simple onClick={refresh} id="refresh">
                <Refresh />
                {t('ad.filters.update')}
              </Button>
            </GridItem>
          </GridContainer>

          <Filter
            filter={filter}
            appliedFilter={appliedFilter}
            restartFilters={restartFilters}
            showCollapseFilters={showCollapseFilters}
            showFilters={this.handleShowFilters}
          />

          <div className={classes.contentContainer}>
            <div
              className={[classes.loadingLineContainer, classes.topLine].join(
                ' ',
              )}
            >
              {fullLoading && (
                <LinearProgress
                  classes={{
                    barColorPrimary: classes.loadingLine,
                  }}
                />
              )}
            </div>
            <GridContainer>
              <GridItem
                className={[
                  classes.groupActions,
                  classes.loteActionsAnimation,
                  selectedItems.length > 0
                    ? classes.showLote
                    : classes.hideLote,
                ].join(' ')}
                xs={12}
              >
                {bulkReviewActions.map(action =>
                  this.renderBulkAction(action, edges),
                )}
              </GridItem>
              <GridItem
                className={[
                  classes.z2,
                  classes.loteActionsAnimation,
                  selectedItems.length > 0 ? classes.hideLote : '',
                ].join(' ')}
                xs={12}
              >
                <GridContainer justify="flex-end" alignItems="center">
                  <GridItem xs={12} sm={8} md={6} lg={4}>
                    {!fullLoading && reviewsData ? (
                      <Paginator
                        pageInfo={reviewsData.pageInfo}
                        changePage={changePage}
                        changeNumberOfRows={changeNumberOfRows}
                        paginationDirection={paginationDirection}
                        numberOfRowsSelected={numberOfRowsSelected}
                      />
                    ) : (
                      <ContentLoader height={67} width={327} speed={2}>
                        <path d={paginationLoaderPath} fill="#D8D8D8" />
                      </ContentLoader>
                    )}
                  </GridItem>
                </GridContainer>
              </GridItem>
              <GridItem xs={12} className={classes.z3}>
                <GridContainer>
                  <GridItem xs={12}>
                    {loading ? (
                      <TableLoader />
                    ) : (
                      <ReactTable
                        data={rows}
                        filterable
                        columns={cols}
                        loading={loading}
                        pageSize={
                          edges.length < numberOfRowsSelected
                            ? edges.length
                            : numberOfRowsSelected
                        }
                        showPagination={false}
                        className={['-striped -highlight', classes.tbl].join(
                          ' ',
                        )}
                        expanded={loadingRows}
                        SubComponent={() => (
                          <div className={classes.loading}>
                            <LoadingAnimation />
                          </div>
                        )}
                        getTrProps={(state, rowInfo) => ({
                          style: {
                            backgroundColor:
                              rowInfo &&
                              selectedItems.indexOf(rowInfo.original.id) !== -1
                                ? 'rgba(171, 221, 250, .8)'
                                : '',
                            boxShadow:
                              rowInfo &&
                              failedIds.indexOf(rowInfo.original.id) !== -1
                                ? 'inset 4px 0 0 red'
                                : '',
                          },
                        })}
                        noDataText={t('reviews.list.noData')}
                        minRows={8}
                      />
                    )}
                  </GridItem>
                </GridContainer>
                <GridContainer justify="flex-end" alignItems="center">
                  <GridItem xs={12} sm={8} md={6} lg={4}>
                    {!fullLoading && reviewsData ? (
                      <Paginator
                        pageInfo={reviewsData.pageInfo}
                        changePage={changePage}
                        changeNumberOfRows={changeNumberOfRows}
                        paginationDirection={paginationDirection}
                        numberOfRowsSelected={numberOfRowsSelected}
                      />
                    ) : null}
                  </GridItem>
                </GridContainer>
              </GridItem>
            </GridContainer>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

ReviewList.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  itemProcessing: PropTypes.instanceOf(Array).isRequired,
  data: PropTypes.shape({}),
  numberOfRowsSelected: PropTypes.number.isRequired,
  paginationDirection: PropTypes.string,
  publishReviews: PropTypes.func.isRequired,
  unpublishReviews: PropTypes.func.isRequired,
  reprocessReviews: PropTypes.func.isRequired,
  sendReviewsToReview: PropTypes.func.isRequired,
  deleteReviews: PropTypes.func.isRequired,
  changePage: PropTypes.func.isRequired,
  changeNumberOfRows: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  fullLoading: PropTypes.bool.isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
  restartFilters: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  failedIds: PropTypes.instanceOf(Array).isRequired,
  showNotification: PropTypes.bool.isRequired,
  closeNotification: PropTypes.func.isRequired,
  authContext: PropTypes.shape({}),
};

ReviewList.defaultProps = {
  authContext: { allPermissions: [] },
  data: null,
  paginationDirection: null,
};

const ReviewWrapper = props => (
  <AuthContext.Consumer>
    {authContext => <ReviewList {...props} authContext={authContext} />}
  </AuthContext.Consumer>
);

export default withTranslation()(
  withStyles(extendedTablesStyle)(ReviewWrapper),
);
